'use client'

import {
  AuthErrorMessage,
  AuthSubmitButton,
  AuthTextField,
} from '@/features/auth/AuthFormControls'
import { AuthSplitLayout } from '@/features/auth/AuthPageLayout'
import { loginWithPostHogSession } from '@/lib/apis/authentication-client'
import { translateAuthenticationError } from '@/lib/utils'
import { Form, Formik } from 'formik'
import Link from 'next/link'
import { useState } from 'react'
import { object, string, ValidationError } from 'yup'
import SocialProviderChooser from '@/features/auth/SocialProviderChooser'
import useSocialAuthentication from '@/features/auth/useSocialAuthentication'
import { socialAuthenticationErrorMessage } from '@/lib/auth/social-auth'

type Props = {
  redirect: string
  socialError?: string
}

const validationSchema = object({
  username: string().required('Email is required'),
  password: string().required('Password is required'),
})

export default function LoginForm({ redirect, socialError }: Props) {
  const [message, setMessage] = useState('')
  const socialAuthentication = useSocialAuthentication()
  const displayedMessage =
    message ||
    (socialAuthentication.error
      ? socialAuthenticationErrorMessage(socialAuthentication.error)
      : socialError
        ? socialAuthenticationErrorMessage(socialError)
        : '')

  return (
    <Formik
      initialValues={{ username: '', password: '' }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          validationSchema.validateSync(values)
        } catch (err) {
          if (err instanceof ValidationError) {
            setMessage(err.message)
            setSubmitting(false)
            return
          }
        }

        setMessage('')

        const formData = new FormData()
        formData.append('username', values.username)
        formData.append('password', values.password)
        formData.append('redirect', 'true')
        formData.append('redirectTo', redirect)

        const err = await loginWithPostHogSession(formData)
        if (err) {
          setMessage(
            err === 'INVALID_CREDENTIALS'
              ? "That email and password don't match."
              : translateAuthenticationError(err),
          )
          setSubmitting(false)
        }
      }}
    >
      {(props) => (
        <AuthSplitLayout
          illustrationTitle="Good to see you again."
          illustrationBody="Your consults, scripts and results are where you left them."
        >
          <h1 className="text-2xl font-bold leading-[30px] text-[#0b0b0f] lg:text-[26px] lg:leading-[33px] lg:tracking-[-0.01em]">
            Sign in
          </h1>

          <Form className="mt-6 flex flex-col lg:mt-5">
            <AuthTextField
              label="Email"
              name="username"
              type="email"
              autoComplete="email"
              placeholder="you@email.com"
              hasError={!!displayedMessage}
            />
            <AuthTextField
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              hasError={!!message}
              className="mt-[18px] lg:mt-5"
              action={
                <Link
                  href="/forgot-password"
                  className="text-[13px] font-semibold leading-[18px] text-[#2563eb] hover:underline"
                >
                  Forgot password?
                </Link>
              }
            />
            {displayedMessage && (
              <AuthErrorMessage
                className="mt-[18px] lg:mt-5"
                action={
                  <Link href="/forgot-password" className="hover:underline">
                    Reset password →
                  </Link>
                }
              >
                {displayedMessage}
              </AuthErrorMessage>
            )}
            <AuthSubmitButton
              loading={props.isSubmitting}
              className={displayedMessage ? 'mt-[18px] lg:mt-6' : 'mt-6'}
            >
              Sign in
            </AuthSubmitButton>
          </Form>

          <SocialProviderChooser
            action="sign-in"
            disabled={props.isSubmitting}
            onSelect={(provider) =>
              socialAuthentication.start(provider, {
                returnTo: redirect,
                errorTo: `/login?redirect=${encodeURIComponent(redirect)}`,
              })
            }
            pendingProvider={socialAuthentication.pendingProvider}
          />

          <p className="mt-5 flex items-center justify-center gap-1.5 text-[13.5px] leading-[18px] text-[#82828b] lg:gap-[5px]">
            New to NextClinic?
            <Link
              className="font-semibold text-[#2563eb] hover:underline"
              href="/signup"
            >
              Create an account
            </Link>
          </p>
        </AuthSplitLayout>
      )}
    </Formik>
  )
}
